import { useNavigate } from 'react-router-dom';
import { useRef, useEffect, useState } from 'react';
import LandingHeader from '../components/organisms/LandingHeader';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

const EnhanceCVLandingPage = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-scroll functionality
  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const maxScroll = container.scrollWidth - container.clientWidth;
        const nextScroll = container.scrollLeft + 350;
        
        if (nextScroll >= maxScroll) {
          // Reset to beginning
          container.scrollTo({ left: 0, behavior: 'smooth' });
          setCurrentIndex(0);
        } else {
          container.scrollBy({ left: 350, behavior: 'smooth' });
          setCurrentIndex(prev => (prev + 1) % 5);
        }
      }
    }, 4000); // Auto-scroll every 4 seconds

    return () => clearInterval(interval);
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -350, behavior: 'smooth' });
      setCurrentIndex(prev => Math.max(0, prev - 1));
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 350, behavior: 'smooth' });
      setCurrentIndex(prev => Math.min(4, prev + 1));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <LandingHeader />
      
      <main className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="space-y-8">
              {/* Stats Banner */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                <Icon name="trending_up" size={18} />
                <span>48,868 resumes created today</span>
              </div>

              {/* Heading */}
              <div>
                <h1 className="text-5xl font-black text-gray-900 mb-4 leading-tight">
                  Build a <span className="text-primary">free resume</span> in a few clicks
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  The first step to a better job? A better resume. Only <span className="font-semibold text-primary">2% of resumes win</span>, and yours will be one of them. Create it now with our free resume builder!
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => navigate('/enhance-cv/create')}
                  className="px-8 py-4 text-lg font-semibold bg-primary hover:bg-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                  Create a New Resume
                </Button>
                <Button
                  onClick={() => navigate('/enhance-cv/improve')}
                  variant="outline"
                  className="px-8 py-4 text-lg font-semibold border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-xl transition-all"
                >
                  Improve My Resume
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-6 pt-6">
                <div className="bg-white rounded-2xl p-6 shadow-soft">
                  <div className="text-4xl font-black text-primary mb-2">48%</div>
                  <div className="text-sm text-gray-600">more likely to get hired</div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-soft">
                  <div className="text-4xl font-black text-purple-600 mb-2">12%</div>
                  <div className="text-sm text-gray-600">better pay with your next job</div>
                </div>
              </div>
            </div>

            {/* Right Side - Resume Preview */}
            <div className="relative">
              {/* Resume Card */}
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-300">
                {/* Header */}
                <div className="flex items-start gap-6 mb-6 pb-6 border-b-2 border-primary">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl text-white font-bold">SW</span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">Samantha Williams</h2>
                    <p className="text-sm text-gray-600 mb-2">Senior Designer</p>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Icon name="email" size={14} />
                        sam@email.com
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="phone" size={14} />
                        202-555-0142
                      </span>
                    </div>
                  </div>
                  {/* ATS Badge */}
                  <div className="flex flex-col gap-2">
                    <button className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                      <Icon name="edit" size={20} className="text-primary" />
                    </button>
                    <button className="p-2 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors">
                      <Icon name="download" size={20} className="text-purple-600" />
                    </button>
                  </div>
                </div>

                {/* Summary Section */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-1 h-5 bg-primary rounded-full"></div>
                    SUMMARY
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Creative and detail-oriented designer with 8+ years of experience in digital product design. 
                    Specialized in creating user-centered designs that drive engagement and conversion.
                  </p>
                </div>

                {/* Experience Section */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-1 h-5 bg-primary rounded-full"></div>
                    EXPERIENCE
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <p className="font-semibold text-gray-900">Lead UI/UX Designer</p>
                          <p className="text-sm text-primary">TechCorp Inc.</p>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">2020 - Present</span>
                      </div>
                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span>Led design team of 5 designers on enterprise SaaS platform</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span>Increased user engagement by 45% through redesign</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <div className="flex justify-between items-start mb-1">
                        <div>
                          <p className="font-semibold text-gray-900">Senior Designer</p>
                          <p className="text-sm text-primary">Creative Studio</p>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">2018 - 2020</span>
                      </div>
                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span>Designed 20+ mobile apps for Fortune 500 clients</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Skills Section */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-1 h-5 bg-primary rounded-full"></div>
                    SKILLS
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['Figma', 'Adobe XD', 'Sketch', 'Prototyping', 'User Research', 'Wireframing'].map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-100 text-primary text-xs font-medium rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* ATS Perfect Badge */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                    <Icon name="verified" className="text-green-600" size={20} />
                    <span className="text-sm font-semibold text-green-700">ATS Perfect</span>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg transform rotate-12">
                ⭐ Professional
              </div>
              <div className="absolute -bottom-4 -left-4 bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                ✨ AI-Powered
              </div>
            </div>
          </div>

          {/* Template Showcase Section */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-gray-900 mb-4">
                Pick a template and build your resume in minutes!
              </h2>
              <p className="text-lg text-gray-600">
                Choose from our professionally designed templates
              </p>
            </div>

            {/* Scrollable Template Gallery */}
            <div className="relative">
              {/* Left Arrow */}
              <button 
                onClick={scrollLeft}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all hover:bg-gray-50"
              >
                <Icon name="chevron_left" size={24} className="text-gray-700" />
              </button>

              <div 
                ref={scrollContainerRef}
                className="flex gap-6 overflow-x-auto pb-8 px-12 snap-x snap-mandatory scrollbar-hide scroll-smooth"
              >

                {/* Template 1 - Classic Angela Balli */}
                <div className="flex-shrink-0 w-[320px] snap-center group">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <div className="h-[440px] bg-white p-6 relative text-[8px] leading-tight overflow-hidden">
                      {/* Header */}
                      <div className="text-center border-b border-gray-300 pb-3 mb-3">
                        <h3 className="text-lg font-bold text-gray-900 tracking-wide">ANGELA BALLI</h3>
                        <p className="text-[9px] text-gray-600 mt-0.5">Marketing Manager</p>
                        <p className="text-[7px] text-gray-500 mt-1">angela@email.com | 555-0123 | New York, NY</p>
                      </div>
                      
                      {/* Summary */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-bold text-gray-900 mb-1">SUMMARY</h4>
                        <p className="text-[7px] text-gray-700 leading-relaxed">
                          Results-driven marketing professional with 7+ years of experience in digital marketing and brand management. 
                          Proven track record of increasing brand awareness and driving revenue growth through innovative campaigns.
                        </p>
                      </div>

                      {/* Experience */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-bold text-gray-900 mb-1">EXPERIENCE</h4>
                        <div className="space-y-2">
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-semibold text-gray-900">Senior Marketing Manager</p>
                              <span className="text-[6px] text-gray-500">2020 - Present</span>
                            </div>
                            <p className="text-[7px] text-gray-600 italic">TechCorp Inc., New York</p>
                            <ul className="text-[7px] text-gray-700 ml-2 mt-0.5 space-y-0.5">
                              <li>• Led team of 8 marketers to achieve 150% increase in lead generation</li>
                              <li>• Managed $2M annual marketing budget across digital channels</li>
                              <li>• Launched 3 successful product campaigns generating $5M revenue</li>
                            </ul>
                          </div>
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-semibold text-gray-900">Marketing Specialist</p>
                              <span className="text-[6px] text-gray-500">2017 - 2020</span>
                            </div>
                            <p className="text-[7px] text-gray-600 italic">Digital Solutions LLC</p>
                            <ul className="text-[7px] text-gray-700 ml-2 mt-0.5 space-y-0.5">
                              <li>• Developed content strategy increasing website traffic by 200%</li>
                              <li>• Managed social media accounts with 50K+ followers</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Education */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-bold text-gray-900 mb-1">EDUCATION</h4>
                        <div>
                          <p className="text-[8px] font-semibold text-gray-900">Bachelor of Business Administration</p>
                          <p className="text-[7px] text-gray-600">New York University | 2013 - 2017</p>
                        </div>
                      </div>

                      {/* Skills */}
                      <div>
                        <h4 className="text-[10px] font-bold text-gray-900 mb-1">SKILLS</h4>
                        <p className="text-[7px] text-gray-700">
                          Digital Marketing • SEO/SEM • Content Strategy • Social Media • Google Analytics • HubSpot • Adobe Creative Suite
                        </p>
                      </div>

                      {/* Hover Button */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button 
                          onClick={() => navigate('/enhance-cv/create')}
                          className="px-6 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 text-sm"
                        >
                          Start With This Template
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 text-center border-t">
                      <p className="font-semibold text-gray-900">Classic</p>
                      <p className="text-xs text-gray-500">Traditional & Professional</p>
                    </div>
                  </div>
                </div>

                {/* Template 2 - Modern Two-Column Aiden Williams */}
                <div className="flex-shrink-0 w-[320px] snap-center group">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <div className="h-[440px] relative text-[8px] leading-tight overflow-hidden">
                      <div className="grid grid-cols-5 h-full">
                        {/* Left Sidebar - Dark */}
                        <div className="col-span-2 bg-slate-700 p-4 text-white">
                          {/* Profile Image */}
                          <div className="w-20 h-20 bg-white rounded-full mx-auto mb-3 flex items-center justify-center overflow-hidden border-2 border-white shadow-lg">
                            <img 
                              src="https://i.pravatar.cc/80?img=12" 
                              alt="Aiden Williams"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<span class="text-white font-bold text-xl">AW</span>';
                              }}
                            />
                          </div>
                          
                          {/* Contact */}
                          <div className="mb-3">
                            <h4 className="text-[9px] font-bold mb-1.5 border-b border-white/30 pb-1">CONTACT</h4>
                            <div className="space-y-1 text-[6px]">
                              <p className="flex items-start gap-1">
                                <span>📧</span>
                                <span className="break-all">aiden@email.com</span>
                              </p>
                              <p className="flex items-start gap-1">
                                <span>📱</span>
                                <span>+1 555-0199</span>
                              </p>
                              <p className="flex items-start gap-1">
                                <span>📍</span>
                                <span>San Francisco, CA</span>
                              </p>
                              <p className="flex items-start gap-1">
                                <span>🔗</span>
                                <span className="break-all">linkedin.com/in/aiden</span>
                              </p>
                            </div>
                          </div>

                          {/* Skills */}
                          <div className="mb-3">
                            <h4 className="text-[9px] font-bold mb-1.5 border-b border-white/30 pb-1">SKILLS</h4>
                            <div className="space-y-1 text-[6px]">
                              <div>
                                <p className="font-semibold mb-0.5">Programming</p>
                                <p>Python, JavaScript, Java, C++</p>
                              </div>
                              <div>
                                <p className="font-semibold mb-0.5">Frameworks</p>
                                <p>React, Node.js, Django, Spring Boot</p>
                              </div>
                              <div>
                                <p className="font-semibold mb-0.5">Tools</p>
                                <p>Git, Docker, AWS, MongoDB</p>
                              </div>
                            </div>
                          </div>

                          {/* Languages */}
                          <div className="mb-3">
                            <h4 className="text-[9px] font-bold mb-1.5 border-b border-white/30 pb-1">LANGUAGES</h4>
                            <div className="text-[6px] space-y-0.5">
                              <p>English - Native</p>
                              <p>Spanish - Fluent</p>
                              <p>French - Basic</p>
                            </div>
                          </div>

                          {/* Certifications */}
                          <div>
                            <h4 className="text-[9px] font-bold mb-1.5 border-b border-white/30 pb-1">CERTIFICATIONS</h4>
                            <div className="text-[6px] space-y-0.5">
                              <p>AWS Solutions Architect</p>
                              <p>Google Cloud Professional</p>
                            </div>
                          </div>
                        </div>

                        {/* Right Content - Light */}
                        <div className="col-span-3 bg-gray-50 p-4">
                          {/* Name & Title */}
                          <div className="mb-3">
                            <h3 className="text-base font-bold text-gray-900">AIDEN WILLIAMS</h3>
                            <p className="text-[10px] text-slate-600 font-semibold">Full Stack Developer</p>
                          </div>

                          {/* Profile */}
                          <div className="mb-3">
                            <h4 className="text-[9px] font-bold text-gray-900 mb-1 border-b-2 border-slate-700 pb-0.5">PROFILE</h4>
                            <p className="text-[7px] text-gray-700 leading-relaxed">
                              Innovative full-stack developer with 5+ years of experience building scalable web applications. 
                              Passionate about clean code and user experience. Proven ability to lead projects from concept to deployment.
                            </p>
                          </div>

                          {/* Experience */}
                          <div className="mb-3">
                            <h4 className="text-[9px] font-bold text-gray-900 mb-1 border-b-2 border-slate-700 pb-0.5">EXPERIENCE</h4>
                            <div className="space-y-2">
                              <div>
                                <div className="flex justify-between items-baseline">
                                  <p className="text-[8px] font-bold text-gray-900">Senior Software Engineer</p>
                                  <span className="text-[6px] text-gray-500">2021 - Present</span>
                                </div>
                                <p className="text-[7px] text-slate-600 italic">Tech Innovations Inc.</p>
                                <ul className="text-[6px] text-gray-700 ml-2 mt-0.5 space-y-0.5">
                                  <li>• Architected microservices handling 1M+ daily requests</li>
                                  <li>• Led team of 6 developers in agile environment</li>
                                  <li>• Reduced API response time by 60% through optimization</li>
                                </ul>
                              </div>
                              <div>
                                <div className="flex justify-between items-baseline">
                                  <p className="text-[8px] font-bold text-gray-900">Software Developer</p>
                                  <span className="text-[6px] text-gray-500">2019 - 2021</span>
                                </div>
                                <p className="text-[7px] text-slate-600 italic">StartupXYZ</p>
                                <ul className="text-[6px] text-gray-700 ml-2 mt-0.5 space-y-0.5">
                                  <li>• Developed RESTful APIs using Node.js and Express</li>
                                  <li>• Built responsive React applications with Redux</li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Education */}
                          <div>
                            <h4 className="text-[9px] font-bold text-gray-900 mb-1 border-b-2 border-slate-700 pb-0.5">EDUCATION</h4>
                            <div>
                              <p className="text-[8px] font-bold text-gray-900">B.S. Computer Science</p>
                              <p className="text-[6px] text-gray-600">Stanford University | 2015 - 2019</p>
                              <p className="text-[6px] text-gray-600">GPA: 3.8/4.0</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Hover Button */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button 
                          onClick={() => navigate('/enhance-cv/create')}
                          className="px-6 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 text-sm"
                        >
                          Start With This Template
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 text-center border-t">
                      <p className="font-semibold text-gray-900">Modern</p>
                      <p className="text-xs text-gray-500">Two-Column Design</p>
                    </div>
                  </div>
                </div>

                {/* Template 3 - Creative Maeve Delaney */}
                <div className="flex-shrink-0 w-[320px] snap-center group">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <div className="h-[440px] bg-gradient-to-br from-green-50 via-white to-blue-50 p-6 relative text-[8px] leading-tight overflow-hidden">
                      {/* Header with Photo */}
                      <div className="flex items-start gap-3 mb-3 pb-3 border-b-2 border-green-400">
                        <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden shadow-lg">
                          <img 
                            src="https://i.pravatar.cc/64?img=5" 
                            alt="Maeve Delaney"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<span class="text-white font-bold text-lg">MD</span>';
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900">Maeve Delaney</h3>
                          <div className="inline-block px-2 py-0.5 bg-green-400 text-white text-[8px] font-semibold rounded mt-1">
                            GRAPHIC DESIGNER
                          </div>
                          <div className="mt-1.5 space-y-0.5 text-[6px] text-gray-600">
                            <p className="flex items-center gap-1">
                              <span className="text-green-600">✉</span> maeve@email.com
                            </p>
                            <p className="flex items-center gap-1">
                              <span className="text-green-600">📱</span> +1 555-0156
                            </p>
                            <p className="flex items-center gap-1">
                              <span className="text-green-600">🌐</span> portfolio.maeve.com
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Profile */}
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center">
                            <span className="text-white text-[10px]">👤</span>
                          </div>
                          <h4 className="text-[10px] font-bold text-gray-900">PROFILE</h4>
                        </div>
                        <p className="text-[7px] text-gray-700 leading-relaxed ml-8">
                          Creative graphic designer with 6+ years of experience in branding, digital design, and illustration. 
                          Passionate about creating visually stunning designs that tell compelling stories and drive engagement.
                        </p>
                      </div>

                      {/* Experience */}
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                            <span className="text-white text-[10px]">💼</span>
                          </div>
                          <h4 className="text-[10px] font-bold text-gray-900">EXPERIENCE</h4>
                        </div>
                        <div className="ml-8 space-y-2">
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-bold text-gray-900">Senior Graphic Designer</p>
                              <span className="text-[6px] text-gray-500">2020 - Present</span>
                            </div>
                            <p className="text-[7px] text-green-600 font-semibold">Creative Agency Pro</p>
                            <ul className="text-[6px] text-gray-700 mt-0.5 space-y-0.5">
                              <li>• Designed brand identities for 30+ clients across various industries</li>
                              <li>• Led creative direction for major campaigns generating $2M revenue</li>
                              <li>• Mentored junior designers and managed design team workflow</li>
                            </ul>
                          </div>
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-bold text-gray-900">Graphic Designer</p>
                              <span className="text-[6px] text-gray-500">2018 - 2020</span>
                            </div>
                            <p className="text-[7px] text-green-600 font-semibold">Design Studio Inc.</p>
                            <ul className="text-[6px] text-gray-700 mt-0.5 space-y-0.5">
                              <li>• Created marketing materials for print and digital platforms</li>
                              <li>• Collaborated with clients to develop unique visual solutions</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Education */}
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center">
                            <span className="text-white text-[10px]">🎓</span>
                          </div>
                          <h4 className="text-[10px] font-bold text-gray-900">EDUCATION</h4>
                        </div>
                        <div className="ml-8">
                          <p className="text-[8px] font-bold text-gray-900">BFA in Graphic Design</p>
                          <p className="text-[6px] text-gray-600">Rhode Island School of Design | 2014 - 2018</p>
                        </div>
                      </div>

                      {/* Skills */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center">
                            <span className="text-white text-[10px]">⚡</span>
                          </div>
                          <h4 className="text-[10px] font-bold text-gray-900">SKILLS</h4>
                        </div>
                        <div className="ml-8 flex flex-wrap gap-1">
                          {['Adobe CC', 'Figma', 'Sketch', 'Illustration', 'Branding', 'Typography', 'UI/UX', 'Print Design'].map((skill, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-gradient-to-r from-green-400 to-blue-400 text-white text-[6px] font-semibold rounded">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Hover Button */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button 
                          onClick={() => navigate('/enhance-cv/create')}
                          className="px-6 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 text-sm"
                        >
                          Start With This Template
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 text-center border-t">
                      <p className="font-semibold text-gray-900">Creative</p>
                      <p className="text-xs text-gray-500">Bold & Colorful</p>
                    </div>
                  </div>
                </div>

                {/* Template 4 - Minimal Ellen Johnson */}
                <div className="flex-shrink-0 w-[320px] snap-center group">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <div className="h-[440px] bg-white p-6 relative text-[8px] leading-tight border border-gray-200 overflow-hidden">
                      {/* Header with Photo */}
                      <div className="flex items-start gap-4 mb-4 pb-3 border-b border-gray-300">
                        <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg">
                          <img 
                            src="https://i.pravatar.cc/64?img=47" 
                            alt="Ellen Johnson"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<span class="text-white font-bold text-lg">EJ</span>';
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-light text-gray-900 tracking-wide">Ellen Johnson</h3>
                          <p className="text-[10px] text-gray-600 mt-0.5">Product Designer</p>
                          <div className="mt-1.5 text-[6px] text-gray-500 space-y-0.5">
                            <p>ellen.johnson@email.com</p>
                            <p>+1 555-0187 | Portland, OR</p>
                            <p>behance.net/ellenjohnson</p>
                          </div>
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-semibold text-gray-900 mb-1 pb-0.5 border-b border-gray-200">SUMMARY</h4>
                        <p className="text-[7px] text-gray-700 leading-relaxed">
                          User-focused product designer with 5+ years of experience creating intuitive digital experiences. 
                          Skilled in user research, wireframing, prototyping, and visual design. Committed to solving complex problems through elegant design solutions.
                        </p>
                      </div>

                      {/* Professional Experience */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-semibold text-gray-900 mb-1 pb-0.5 border-b border-gray-200">PROFESSIONAL EXPERIENCE</h4>
                        <div className="space-y-2">
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-semibold text-gray-900">Senior Product Designer</p>
                              <span className="text-[6px] text-gray-500">2021 - Present</span>
                            </div>
                            <p className="text-[7px] text-gray-600">TechFlow Solutions, Portland, OR</p>
                            <ul className="text-[6px] text-gray-700 ml-3 mt-0.5 space-y-0.5 list-disc">
                              <li>Led design for SaaS platform serving 100K+ users, improving user satisfaction by 40%</li>
                              <li>Conducted user research and usability testing to inform design decisions</li>
                              <li>Collaborated with engineering and product teams in agile environment</li>
                              <li>Established design system reducing development time by 30%</li>
                            </ul>
                          </div>
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-semibold text-gray-900">Product Designer</p>
                              <span className="text-[6px] text-gray-500">2019 - 2021</span>
                            </div>
                            <p className="text-[7px] text-gray-600">Digital Innovations Inc.</p>
                            <ul className="text-[6px] text-gray-700 ml-3 mt-0.5 space-y-0.5 list-disc">
                              <li>Designed mobile and web applications for e-commerce clients</li>
                              <li>Created wireframes, mockups, and interactive prototypes</li>
                              <li>Increased conversion rates by 25% through UX improvements</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Education */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-semibold text-gray-900 mb-1 pb-0.5 border-b border-gray-200">EDUCATION</h4>
                        <div>
                          <p className="text-[8px] font-semibold text-gray-900">Bachelor of Fine Arts in Design</p>
                          <p className="text-[6px] text-gray-600">Portland State University | 2015 - 2019</p>
                          <p className="text-[6px] text-gray-600">Graduated Magna Cum Laude</p>
                        </div>
                      </div>

                      {/* Skills */}
                      <div>
                        <h4 className="text-[10px] font-semibold text-gray-900 mb-1 pb-0.5 border-b border-gray-200">SKILLS & TOOLS</h4>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[6px] text-gray-700">
                          <div>
                            <p className="font-semibold text-gray-900">Design</p>
                            <p>UI/UX Design, Wireframing, Prototyping, User Research</p>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Tools</p>
                            <p>Figma, Sketch, Adobe XD, InVision, Principle</p>
                          </div>
                        </div>
                      </div>

                      {/* Hover Button */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button 
                          onClick={() => navigate('/enhance-cv/create')}
                          className="px-6 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 text-sm"
                        >
                          Start With This Template
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 text-center border-t">
                      <p className="font-semibold text-gray-900">Minimal</p>
                      <p className="text-xs text-gray-500">Clean & Simple</p>
                    </div>
                  </div>
                </div>

                {/* Template 5 - Executive Grace Jackson */}
                <div className="flex-shrink-0 w-[320px] snap-center group">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <div className="h-[440px] bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 p-6 relative text-[8px] leading-tight text-white overflow-hidden">
                      {/* Header */}
                      <div className="text-center mb-4 pb-3 border-b-2 border-orange-400">
                        <h3 className="text-xl font-bold tracking-wider">GRACE JACKSON</h3>
                        <p className="text-[10px] text-orange-400 font-semibold mt-1">EXECUTIVE DIRECTOR</p>
                        <div className="mt-2 text-[6px] text-blue-200 space-y-0.5">
                          <p>grace.jackson@executive.com | +1 555-0199</p>
                          <p>Boston, MA | linkedin.com/in/gracejackson</p>
                        </div>
                      </div>

                      {/* Executive Summary */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-bold text-orange-400 mb-1 tracking-wide">EXECUTIVE SUMMARY</h4>
                        <p className="text-[7px] text-blue-100 leading-relaxed">
                          Strategic executive leader with 15+ years of experience driving organizational growth and operational excellence. 
                          Proven track record of leading cross-functional teams, managing P&L, and delivering results in competitive markets. 
                          Expert in strategic planning, business development, and change management.
                        </p>
                      </div>

                      {/* Professional Experience */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-bold text-orange-400 mb-1 tracking-wide">PROFESSIONAL EXPERIENCE</h4>
                        <div className="space-y-2">
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-bold text-white">Executive Director</p>
                              <span className="text-[6px] text-blue-300">2019 - Present</span>
                            </div>
                            <p className="text-[7px] text-orange-300 font-semibold">Global Enterprises Inc., Boston, MA</p>
                            <ul className="text-[6px] text-blue-100 ml-2 mt-0.5 space-y-0.5">
                              <li>• Led organization of 500+ employees with $50M annual revenue</li>
                              <li>• Increased profitability by 35% through strategic initiatives</li>
                              <li>• Spearheaded digital transformation reducing costs by $5M</li>
                              <li>• Built high-performing leadership team and fostered culture of innovation</li>
                            </ul>
                          </div>
                          <div>
                            <div className="flex justify-between items-baseline">
                              <p className="text-[8px] font-bold text-white">Senior Vice President</p>
                              <span className="text-[6px] text-blue-300">2015 - 2019</span>
                            </div>
                            <p className="text-[7px] text-orange-300 font-semibold">Corporate Solutions LLC</p>
                            <ul className="text-[6px] text-blue-100 ml-2 mt-0.5 space-y-0.5">
                              <li>• Managed $30M P&L and 200+ person team across 3 divisions</li>
                              <li>• Drove 40% revenue growth through new market expansion</li>
                              <li>• Negotiated strategic partnerships generating $10M annually</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Education */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-bold text-orange-400 mb-1 tracking-wide">EDUCATION</h4>
                        <div className="space-y-1">
                          <div>
                            <p className="text-[8px] font-bold text-white">MBA, Executive Leadership</p>
                            <p className="text-[6px] text-blue-200">Harvard Business School | 2014</p>
                          </div>
                          <div>
                            <p className="text-[8px] font-bold text-white">B.S. Business Administration</p>
                            <p className="text-[6px] text-blue-200">Boston University | 2005</p>
                          </div>
                        </div>
                      </div>

                      {/* Core Competencies */}
                      <div className="mb-3">
                        <h4 className="text-[10px] font-bold text-orange-400 mb-1 tracking-wide">CORE COMPETENCIES</h4>
                        <div className="grid grid-cols-2 gap-1">
                          {['Strategic Planning', 'P&L Management', 'Team Leadership', 'Business Development', 'Change Management', 'Operations'].map((skill, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                              <span className="text-[6px] text-blue-100">{skill}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Certifications */}
                      <div>
                        <h4 className="text-[10px] font-bold text-orange-400 mb-1 tracking-wide">CERTIFICATIONS</h4>
                        <div className="text-[6px] text-blue-100 space-y-0.5">
                          <p>• Six Sigma Black Belt</p>
                          <p>• Project Management Professional (PMP)</p>
                          <p>• Certified Executive Coach</p>
                        </div>
                      </div>

                      {/* Hover Button */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button 
                          onClick={() => navigate('/enhance-cv/create')}
                          className="px-6 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 text-sm"
                        >
                          Start With This Template
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 text-center border-t">
                      <p className="font-semibold text-gray-900">Executive</p>
                      <p className="text-xs text-gray-500">Premium & Elegant</p>
                    </div>
                  </div>
                </div>

              </div>

              {/* Right Arrow */}
              <button 
                onClick={scrollRight}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all hover:bg-gray-50"
              >
                <Icon name="chevron_right" size={24} className="text-gray-700" />
              </button>

              {/* Scroll Indicators */}
              <div className="flex justify-center gap-2 mt-6">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div 
                    key={i} 
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentIndex ? 'bg-primary w-8' : 'bg-gray-300'}`}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-4 shadow-lg">
                <Icon name="speed" className="text-white text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Fast & Easy</h3>
              <p className="text-gray-600">
                Create a professional resume in minutes with our intuitive builder
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
                <Icon name="verified" className="text-white text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">ATS-Friendly</h3>
              <p className="text-gray-600">
                Optimized for Applicant Tracking Systems to get past the bots
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl mb-4 shadow-lg">
                <Icon name="auto_awesome" className="text-white text-3xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI-Enhanced</h3>
              <p className="text-gray-600">
                Get smart suggestions and improvements powered by artificial intelligence
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EnhanceCVLandingPage;
